// src/components/BookingForm.jsx

import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
  Link,
  Collapse,
  Chip,
} from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import moment from "moment-timezone";
import { todayInOrgTimezone } from "../utils/timezoneUtils";

import {
  createBooking,
  getFreeSlots,
  getBookingsByRoom,
} from "../services/bookingService";
import { getCurrentUser } from "../services/authService";
import { useTimezone } from "../contexts/TimezoneContext";

function BookingForm({
  roomId,
  onBookingSuccess,
  onBookingError,
  selectedDate,
  onDateChange,
}) {
  const [userId, setUserId] = useState("");
  const { orgTimeZone } = useTimezone();

  const [availableSlots, setAvailableSlots] = useState([]);
  const [startSlot, setStartSlot] = useState("");
  const [endSlot, setEndSlot] = useState("");
  const [bookings, setBookings] = useState([]);

  // מצבי "קיפול"
  const [showDatePicker, setShowDatePicker] = useState(true);
  const [showStartPicker, setShowStartPicker] = useState(false);
  const [showEndPicker, setShowEndPicker] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    getCurrentUser()
      .then((u) => setUserId(u._id))
      .catch(() => onBookingError("Please login first"));
  }, [onBookingError]);

  // Date change handler
  async function handleDateChangeInternal(newDate) {
    // Debug logging to verify date selection is working correctly
    if (newDate) {
      console.log('🗓️ Date selected:');
      console.log('  Selected date object:', newDate);
      console.log('  Formatted for API:', formatDateYYYYMMDD(newDate));
      console.log('  Display format:', formatDateToDisplay(newDate));
    }
    
    if (onDateChange) {
      onDateChange(newDate);
    }
    // reset
    setStartSlot("");
    setEndSlot("");
    setAvailableSlots([]);

    if (!newDate) return;

    const dateStr = formatDateYYYYMMDD(newDate);
    try {
      const freeSlots = await getFreeSlots(roomId, dateStr);
      setAvailableSlots(freeSlots);

      const allB = await getBookingsByRoom(roomId);
      setBookings(allB);
    } catch (err) {
      onBookingError("Error fetching data");
      console.error(err);
    }

    // after picking a date => hide date picker, show start
    setShowDatePicker(false);
    setShowStartPicker(true);
    setShowEndPicker(false);
  }

  function formatDateYYYYMMDD(d) {
    // Extract pure date (YYYY-MM-DD) without timezone conversion
    // This ensures that if user clicks June 30, we get "2024-06-30" regardless of timezone
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function combineDateAndTime(dateObj, hhmm) {
    // Extract the pure date components (year, month, day) from dateObj
    // and combine with time in organization timezone to avoid date shifting
    const [hh, mm] = hhmm.split(":").map(Number);
    
    // Get the date components without timezone conversion
    const year = dateObj.getFullYear();
    const month = dateObj.getMonth(); // 0-based
    const day = dateObj.getDate();
    
    // Create a moment in organization timezone for this specific date and time
    const combinedMoment = moment.tz({
      year,
      month,
      date: day,
      hour: hh,
      minute: mm,
      second: 0,
      millisecond: 0
    }, orgTimeZone);
    
    return combinedMoment.toDate();
  }

  function isSameDay(d1, d2) {
    // For comparing booking dates (from database) with selected date
    // Convert booking date to organization timezone and extract date parts
    const bookingDate = moment.tz(d1, orgTimeZone);
    const selectedDate = new Date(d2);
    
    return (
      bookingDate.year() === selectedDate.getFullYear() &&
      bookingDate.month() === selectedDate.getMonth() &&
      bookingDate.date() === selectedDate.getDate()
    );
  }

  function nextHalfHour(slot) {
    const [hh, mm] = slot.split(":").map(Number);
    let newMm = mm + 30;
    let newHh = hh;
    if (newMm === 60) {
      newHh += 1;
      newMm = 0;
    }
    return `${String(newHh).padStart(2, "0")}:${String(newMm).padStart(2, "0")}`;
  }

  function handleStartSlotClick(slot) {
    setStartSlot(slot);
    setEndSlot("");
    // after picking start => hide start, show end
    setShowStartPicker(false);
    setShowEndPicker(true);
  }

  function getEndSlotOptions() {
    if (!startSlot || !selectedDate) return [];
    const startTime = combineDateAndTime(selectedDate, startSlot);

    let overlapBookingEnd = null;
    let nextBookingStart = null;

    for (const b of bookings) {
      // Convert booking times to organization timezone for comparison
      const bStart = moment.tz(b.startTime, orgTimeZone).toDate();
      const bEnd = moment.tz(b.endTime, orgTimeZone).toDate();

      if (isSameDay(bStart, startTime) || isSameDay(bEnd, startTime)) {
        if (bStart <= startTime && startTime < bEnd) {
          if (!overlapBookingEnd || bEnd < overlapBookingEnd) {
            overlapBookingEnd = bEnd;
          }
        }
        if (bStart > startTime) {
          if (!nextBookingStart || bStart < nextBookingStart) {
            nextBookingStart = bStart;
          }
        }
      }
    }

    let cutoffTime = overlapBookingEnd || nextBookingStart || null;
    const results = [];
    let curSlot = startSlot;

    while (true) {
      curSlot = nextHalfHour(curSlot);
      const slotTime = combineDateAndTime(selectedDate, curSlot);

      if (cutoffTime && slotTime.getTime() === cutoffTime.getTime()) {
        results.push(curSlot);
        break;
      }
      if (!availableSlots.includes(curSlot)) break;
      if (cutoffTime && slotTime > cutoffTime) break;
      results.push(curSlot);
    }
    return results;
  }

  function handleEndSlotClick(slot) {
    setEndSlot(slot);
    setShowEndPicker(false);
  }

  async function handleSubmit() {
    if (!selectedDate) {
      onBookingError("Please select a date");
      return;
    }
    if (!startSlot || !endSlot) {
      onBookingError("Please select start and end times");
      return;
    }
    const startTime = combineDateAndTime(selectedDate, startSlot);
    const endTime = combineDateAndTime(selectedDate, endSlot);

    if (endTime <= startTime) {
      onBookingError("End time must be after start time");
      return;
    }

    const hasOverlap = bookings.some((b) => {
      // Convert booking times to organization timezone for comparison
      const bStart = moment.tz(b.startTime, orgTimeZone).toDate();
      const bEnd = moment.tz(b.endTime, orgTimeZone).toDate();
      if (isSameDay(bStart, startTime)) {
        return !(endTime <= bStart || startTime >= bEnd);
      }
      return false;
    });
    if (hasOverlap) {
      onBookingError("This time range is already booked");
      return;
    }

    try {
      await createBooking({ roomId, userId, startTime, endTime });
      onBookingSuccess("Booking created successfully");

      // reset
      setStartSlot("");
      setEndSlot("");
      setShowDatePicker(true);
      setShowStartPicker(false);
      setShowEndPicker(false);

      if (selectedDate) {
        const dateStr = formatDateYYYYMMDD(selectedDate);
        const updatedSlots = await getFreeSlots(roomId, dateStr);
        setAvailableSlots(updatedSlots);

        const updatedBookings = await getBookingsByRoom(roomId);
        setBookings(updatedBookings);
      }
    } catch (err) {
      console.error("Booking error", err);
      onBookingError("Error creating booking");
    }
  }

  function renderSlotButtons(slots, selectedValue, onClickSlot) {
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
        {slots.map((slot) => {
          const isSelected = slot === selectedValue;
          return (
            <Button
              key={slot}
              variant={isSelected ? "contained" : "outlined"}
              onClick={() => onClickSlot(slot)}
              sx={{ minWidth: 64 }}
            >
              {slot}
            </Button>
          );
        })}
      </Box>
    );
  }

  // Format date for display without timezone conversion to avoid date shifting
  function formatDateToDisplay(dateObj) {
    if (!dateObj) return "";
    // Use the date as-is from the calendar selection to avoid shifting
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                       "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = monthNames[dateObj.getMonth()];
    const day = dateObj.getDate();
    const year = dateObj.getFullYear();
    return `${month} ${day.toString().padStart(2, '0')}, ${year}`;
  }

  return (
    <Paper sx={{ p: 2, mt: 2 }} elevation={2}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Typography variant="h6">
          Book a Meeting
        </Typography>
        <Chip 
          label={`Times in ${orgTimeZone}`} 
          size="small" 
          variant="outlined" 
          color="primary"
        />
      </Box>

      {/* Step 1: Date Selection */}
      <Box sx={{ mb: 2 }}>
        <Collapse in={showDatePicker} timeout={300}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateCalendar
              value={selectedDate || null}
              onChange={handleDateChangeInternal}
              minDate={todayInOrgTimezone(orgTimeZone)}
            />
          </LocalizationProvider>
        </Collapse>

        {/* If chosen date => summary + small link */}
        {!showDatePicker && selectedDate && (
          <Box sx={{ mt: 1 }}>
            <Typography>
              <strong>Date:</strong> {formatDateToDisplay(selectedDate)}
              {" "}
              <Link
                component="button"
                variant="body2"
                sx={{ ml: 1 }}
                onClick={() => {
                  if (onDateChange) onDateChange(null);
                  setAvailableSlots([]);
                  setBookings([]);
                  setStartSlot("");
                  setEndSlot("");
                  setShowDatePicker(true);
                  setShowStartPicker(false);
                  setShowEndPicker(false);
                }}
              >
                Change
              </Link>
            </Typography>
          </Box>
        )}
      </Box>

      {/* Step 2: Start Time */}
      {selectedDate && (
        <Box sx={{ mb: 2 }}>
          <Collapse in={showStartPicker} timeout={300}>
            <Box>
              <Typography variant="subtitle1">Select Start Time:</Typography>
              {renderSlotButtons(availableSlots, startSlot, handleStartSlotClick)}
            </Box>
          </Collapse>

          {/* Summary if chosen */}
          {!showStartPicker && startSlot && (
            <Typography sx={{ mt: 1 }}>
              <strong>Start Time:</strong> {startSlot}
              {" "}
              <Link
                component="button"
                variant="body2"
                sx={{ ml: 1 }}
                onClick={() => {
                  setStartSlot("");
                  setEndSlot("");
                  setShowStartPicker(true);
                  setShowEndPicker(false);
                }}
              >
                Change
              </Link>
            </Typography>
          )}
        </Box>
      )}

      {/* Step 3: End Time */}
      {selectedDate && startSlot && (
        <Box sx={{ mb: 2 }}>
          <Collapse in={showEndPicker} timeout={300}>
            <Box>
              <Typography variant="subtitle1">Select End Time:</Typography>
              {renderSlotButtons(getEndSlotOptions(), endSlot, handleEndSlotClick)}
            </Box>
          </Collapse>

          {!showEndPicker && endSlot && (
            <Typography sx={{ mt: 1 }}>
              <strong>End Time:</strong> {endSlot}
              {" "}
              <Link
                component="button"
                variant="body2"
                sx={{ ml: 1 }}
                onClick={() => {
                  setEndSlot("");
                  setShowEndPicker(true);
                }}
              >
                Change
              </Link>
            </Typography>
          )}
        </Box>
      )}

      {/* Step 4: Create Booking */}
      {selectedDate && startSlot && endSlot && (
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!userId}
        >
          Create Booking
        </Button>
      )}
    </Paper>
  );
}

export default BookingForm;